import { Component, ElementRef, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { faTimes, faChevronDown, faSearch, faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { SuccessModalComponent } from '../success-modal/success-modal.component';
import { countryCodesAndFlags } from '../../shared/utils/constants';
import { idNumberValidator, expiryDateValidator, issueDateValidator } from '../../shared/validators/id-document.validators';
import { TravelDocumentService } from 'projects/clooper/core/src/public-api';
import { UpdateTravelDocumentDto } from 'projects/clooper/core/src/lib/models/travel-document';

@Component({
  selector: 'app-edit-document-modal',
  templateUrl: './edit-document-modal.component.html',
  styleUrls: ['./edit-document-modal.component.scss']
})
export class EditDocumentModalComponent implements OnInit {
  icons = { close: faTimes, chevronDown: faChevronDown, search: faSearch, warn: faExclamationTriangle };
  documentForm: FormGroup;
  nameMismatchWarning = '';

  // These properties are set via initialState when the modal is opened
  _id: string;
  country: string;
  type: string;
  first_name: string;
  last_name: string;
  id_number: string;
  expiry_date: string;
  issue_date: string;
  profileFirstName = '';
  profileLastName = '';
  onEdited: () => void;
  isSubmitting = false;

  private readonly TYPE_LABEL_TO_API: Record<string, string> = {
    'International Passport': 'passport',
    'National ID': 'national_id',
    "Driver's License": 'drivers_license',
    'Visa': 'visa',
    'Residence Permit': 'residence_permit'
  };

  countries = countryCodesAndFlags;
  filteredCountries = countryCodesAndFlags;
  countrySearch = '';
  showCountryDropdown = false;
  selectedCountry: any = null;


  documentTypes: string[] = [
    'International Passport', 'National ID', 'Driver\'s License', 'Visa', 'Residence Permit'
  ];

  constructor(
    public bsModalRef: BsModalRef,
    private fb: FormBuilder,
    private modalService: BsModalService,
    private elRef: ElementRef,
    private travelDocumentService: TravelDocumentService
  ) {}

  ngOnInit(): void {
    const formattedExpiry = this.formatDateForInput(this.expiry_date);
    const formattedIssue = this.formatDateForInput(this.issue_date);

    this.documentForm = this.fb.group({
      country: [this.country || '', Validators.required],
      type: [this.type || '', Validators.required],
      first_name: [this.first_name || '', [Validators.required, Validators.minLength(1)]],
      last_name: [this.last_name || '', [Validators.required, Validators.minLength(1)]],
      id_number: [this.id_number || '', [
        Validators.required,
        idNumberValidator(
          () => this.documentForm?.get('type')?.value || '',
          () => this.selectedCountry?.code || ''
        )
      ]],
      expiry_date: [formattedExpiry, [Validators.required, expiryDateValidator]],
      issue_date: [formattedIssue, [
        issueDateValidator(() => this.documentForm?.get('expiry_date')?.value || '')
      ]],
    });

    // Pre-select country if editing
    if (this.country) {
      const normalizedCountry = this.country?.trim().toLowerCase();
      this.selectedCountry = this.countries.find(c => {
        const name = c?.name?.toLowerCase();
        const code = c?.code?.toLowerCase();
        return name === normalizedCountry || code === normalizedCountry;
      }) || null;
    }

    // Re-validate id_number when type changes
    this.documentForm.get('type')?.valueChanges.subscribe(() => {
      this.documentForm.get('id_number')?.updateValueAndValidity();
    });
  }

  checkNameMismatch(): void {
    if (!this.profileFirstName && !this.profileLastName) {
      this.nameMismatchWarning = '';
      return;
    }
    const fn = (this.documentForm.get('first_name')?.value || '').trim().toLowerCase();
    const ln = (this.documentForm.get('last_name')?.value || '').trim().toLowerCase();
    const pfn = this.profileFirstName.trim().toLowerCase();
    const pln = this.profileLastName.trim().toLowerCase();
    if (fn && ln && (fn !== pfn || ln !== pln)) {
      this.nameMismatchWarning = 'The name on this document does not match your profile name. Please verify it is correct.';
    } else {
      this.nameMismatchWarning = '';
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.elRef.nativeElement.querySelector('.country-dropdown-wrapper')?.contains(event.target)) {
      this.showCountryDropdown = false;
    }
  }

  toggleCountryDropdown(): void {
    this.showCountryDropdown = !this.showCountryDropdown;
    if (this.showCountryDropdown) {
      this.countrySearch = '';
      this.filteredCountries = this.countries;
    }
  }

  filterCountries(): void {
    const term = this.countrySearch.toLowerCase();
    this.filteredCountries = this.countries.filter(
      c => c.name?.toLowerCase().includes(term)
    );
  }

  getCountryFlagUrl(country: any): string {
    const code = (country?.code || '').toUpperCase();
    return code ;
  }

  onFlagError(event: Event, countryCode: string): void {
    const img = event.target as HTMLImageElement | null;
    if (!img) return;

    const fallbackUrl = countryCode ? `https://flagcdn.com/w40/${countryCode.toLowerCase()}.png` : '';
    if (fallbackUrl && img.src !== fallbackUrl) {
      img.src = fallbackUrl;
      return;
    }

    img.style.visibility = 'hidden';
  }

  selectCountry(country: any): void {
    this.selectedCountry = country;
    this.documentForm.get('country')?.setValue(country.name.trim().toLowerCase());
    this.showCountryDropdown = false;
    this.documentForm.get('id_number')?.updateValueAndValidity();
  }

  private formatDateForInput(date: string): string {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return date;
    return d.toISOString().substring(0, 10);
  }

  close(): void {
    this.bsModalRef?.hide();
  }

  saveChanges(): void {
    if (this.documentForm.valid && !this.isSubmitting) {
      this.isSubmitting = true;
      const formVal = this.documentForm.value;
      const updates: UpdateTravelDocumentDto = {
        country: this.selectedCountry?.code?.toLowerCase() || formVal.country.trim().toLowerCase(),
        type: this.TYPE_LABEL_TO_API[formVal.type] || formVal.type,
        firstName: formVal.first_name,
        lastName: formVal.last_name,
        documentNumber: formVal.id_number,
        expirationDate: formVal.expiry_date
      };
      this.travelDocumentService.updateTravelDocumentFull(this._id, updates).subscribe({
        next: () => {
          this.isSubmitting = false;
          if (this.onEdited) this.onEdited();
          this.close();
          this.modalService.show(SuccessModalComponent, {
            initialState: {
              title: 'Document Updated!',
              subtitle: 'Your travel document has been updated successfully.'
            },
            class: 'modal-rounded'
          });
        },
        error: () => {
          this.isSubmitting = false;
        }
      });
    }
  }
}
